import React from "react";
import { animated, useTransition } from "@react-spring/web";
import cx from "classnames";
import Actions from "src/components/actionbar";
import { StoryboardSettingsTab } from "../tabs";
import { LeadsDownloadButton } from "./LeadsDownloadButton";
import styles from "./actionBar.module.scss";

const NO_PERMISSION = "You don't have permission";

interface ActionBarProps {
  show: boolean;
  published: boolean;
  canEditAccess: boolean;
  canManageAccess: boolean;
  isUnpublishing?: boolean;
  isDeleting?: boolean;
  hasLiveUrl: boolean;
  activeTab: StoryboardSettingsTab;
  storyboardId: string;
  canPreview: boolean;
  canUnpublish: boolean;
  canEmbed: boolean;
  onPreview: () => void;
  onCopyLink: () => void;
  onEditSite: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
  onCode: () => void;
  onInvite: () => void;
  onClone: () => void;
}

const ActionBar: React.FC<ActionBarProps> = ({
  show,
  published,
  canEditAccess,
  canManageAccess,
  isUnpublishing,
  isDeleting,
  hasLiveUrl,
  activeTab,
  storyboardId,
  canPreview,
  canUnpublish,
  canEmbed,
  onPreview,
  onCopyLink,
  onEditSite,
  onUnpublish,
  onDelete,
  onCode,
  onInvite,
  onClone,
}) => {
  const transitions = useTransition(show, {
    initial: { opacity: 0, top: "0px" },
    from: { opacity: 1, top: "-90px" },
    enter: { opacity: 1, top: "0px" },
    leave: { opacity: 0, top: "-90px" },
  });
  
  const DISABLED_ACTION_TABS: StoryboardSettingsTab[] = ["rolesPermissions"];
  const isActionsDisabled = DISABLED_ACTION_TABS.includes(activeTab);
  const isCompact = !canUnpublish;
  const embedHidden = !canEmbed;
  const both = !canUnpublish && !canEmbed;

  return transitions(
    (style, item) =>
      item && (
        <animated.div
          style={style}
          className={cx(styles.actionBar, styles.actionBarContainer)}
        >
          <Actions.Container>
            <Actions.Row>
              <Actions.Button
                width={isCompact && !both ? 3 : 2}
                icon="paperflite-edit"
                title="Edit site"
                disabled={!canEditAccess}
                tooltip={canEditAccess ? undefined : NO_PERMISSION}
                onClick={onEditSite}
              />
              <Actions.Button
                width={isCompact ? 2 : 1}
                icon="paperflite-eye"
                title={isCompact ? "Preview" : ""}
                disabled={!canPreview}
                tooltip={
                  !canPreview
                    ? "Preview unavailable"
                    : isCompact
                      ? ""
                      : "Preview"
                }
                onClick={onPreview}
                className={isCompact ? styles.previewActionButton : undefined}
              />
              {canUnpublish && (
                <Actions.Button
                  width={2}
                  icon="paperflite-broken-link"
                  title={isUnpublishing ? "Unpublishing…" : "Unpublish"}
                  disabled={!published || isUnpublishing || !canManageAccess || isActionsDisabled}
                  tooltip={isActionsDisabled ? undefined : canManageAccess ? undefined : NO_PERMISSION}
                  onClick={onUnpublish}
                  className={styles.unpublishActionButton}
                />
              )}
              <Actions.IconButton
                icon="paperflite-delete"
                tooltip={isActionsDisabled ? undefined : canManageAccess ? "Delete" : NO_PERMISSION}
                disabled={!canManageAccess || isDeleting || isActionsDisabled}
                onClick={onDelete}
              />
            </Actions.Row>
            <Actions.Row>
              {canEmbed && (
                <Actions.IconButton
                  icon="paperflite-code"
                  tooltip={
                    !canEditAccess
                      ? NO_PERMISSION
                      : hasLiveUrl
                        ? "Copy embed code"
                        : "Embed unavailable"
                  }
                  disabled={!canEditAccess || !hasLiveUrl}
                  onClick={onCode}
                />
              )}
              <Actions.Button
                width={embedHidden && !both ? 2 : 1}
                icon="paperflite-clone"
                title={embedHidden && !both ? "Clone" : ""}
                disabled={!canEditAccess || isActionsDisabled}
                tooltip={isActionsDisabled ? undefined : !canEditAccess ? NO_PERMISSION : embedHidden && !both ? "" : "Clone"}
                onClick={onClone}
                className={embedHidden && !both ? styles.cloneActionButton : undefined}
              />
              <Actions.IconButton
                icon="paperflite-link"
                tooltip={
                  !canEditAccess
                    ? NO_PERMISSION
                    : hasLiveUrl
                      ? "Copy link"
                      : "Copy link unavailable"
                }
                disabled={!canEditAccess || !hasLiveUrl}
                onClick={onCopyLink}
              />
              {activeTab === "leadsAndReports" ? (
                <LeadsDownloadButton storyboardId={storyboardId} disabled={!canEditAccess} />
              ) : (
                <Actions.Button
                  width={3}
                  icon="paperflite-add-assignee"
                  title="Add User"
                  disabled={!canManageAccess}
                  tooltip={
                    !canManageAccess ? "You don't have permission" : undefined
                  }
                  onClick={onInvite}
                  className={styles.addUserActionButton}
                />
              )}
            </Actions.Row>
          </Actions.Container>
        </animated.div>
      ),
  );
};

export default ActionBar;
